import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// Les URLs de Supabase
const supabaseUrl = 'https://yrvcsfoocohtzwnkopsz.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlydmNzZm9vY29odHp3bmtvcHN6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE0NzYyMTYsImV4cCI6MjA1NzA1MjIxNn0.Z34cG8rQz6vF-ZjLyy0NShRh4K5j6r3GCGckfgyQku4';

// Nom du bucket pour les photos des profils d'annonce
export const PROFIL_ANNONCE_BUCKET = 'profilannoncephotos';

// Création du client Supabase sans options de fetch personnalisées
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Configurer la gestion d'événements pour l'authentification
supabase.auth.onAuthStateChange((event, session) => {
  console.log(`[Supabase] Événement auth: ${event}`, session ? 'Session active' : 'Pas de session');
  
  if (event === 'SIGNED_IN') {
    console.log('[Supabase] Utilisateur connecté, session stockée');
    
    // Vérifier s'il y a une URL de redirection stockée
    const redirectTo = localStorage.getItem('redirectAfterAuth');
    if (redirectTo) {
      console.log('[Supabase] Redirection après connexion vers:', redirectTo);
      localStorage.removeItem('redirectAfterAuth');
      window.location.href = redirectTo;
    }
  }
  
  if (event === 'SIGNED_OUT') {
    console.log('[Supabase] Utilisateur déconnecté');
  }
  
  if (event === 'TOKEN_REFRESHED') {
    console.log('[Supabase] Token rafraîchi');
  }
});

/**
 * Télécharge une image pour un profil annonce et retourne l'URL
 * @param file Fichier à télécharger
 * @param profilId ID du profil annonce
 * @returns URL de l'image téléchargée ou null en cas d'erreur
 */
export const uploadProfilAnnoncePhoto = async (file: File, profilId: string): Promise<string | null> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${profilId}_${Date.now()}.${fileExt}`;
    const filePath = `${profilId}/${fileName}`;
    
    console.log(`[Supabase] Téléchargement d'image de profil annonce: ${filePath}`);
    
    const { data, error } = await supabase.storage
      .from(PROFIL_ANNONCE_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('[Supabase] Erreur lors du téléchargement de l\'image:', error);
      return null;
    }
    
    // Récupérer l'URL publique de l'image
    const { data: { publicUrl } } = supabase.storage
      .from(PROFIL_ANNONCE_BUCKET)
      .getPublicUrl(data.path);
    
    return publicUrl;
  } catch (error) {
    console.error('[Supabase] Exception lors du téléchargement de l\'image:', error);
    return null;
  }
};

/**
 * Supprime une photo de profil annonce
 * @param photoUrl URL de la photo à supprimer
 * @returns true si succès, false sinon
 */
export const deleteProfilAnnoncePhoto = async (photoUrl: string): Promise<boolean> => {
  try {
    // Extraire le chemin du fichier de l'URL
    const urlParts = photoUrl.split(`${PROFIL_ANNONCE_BUCKET}/`);
    if (urlParts.length < 2) {
      console.error('[Supabase] Format d\'URL invalide:', photoUrl);
      return false;
    }
    
    const filePath = urlParts[1];
    console.log(`[Supabase] Suppression d'image de profil annonce: ${filePath}`);
    
    const { error } = await supabase.storage
      .from(PROFIL_ANNONCE_BUCKET)
      .remove([filePath]);
    
    if (error) {
      console.error('[Supabase] Erreur lors de la suppression de l\'image:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('[Supabase] Exception lors de la suppression de l\'image:', error);
    return false;
  }
};

// Exporter un helper pour tester la connexion Supabase
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    const start = Date.now();
    const { error } = await supabase.from('app_settings').select('count', { count: 'exact', head: true });
    const elapsed = Date.now() - start;
    
    console.log(`[Supabase] Test de connexion: ${error ? 'Échec' : 'Succès'} en ${elapsed}ms`);
    return !error;
  } catch (error) {
    console.error('[Supabase] Erreur lors du test de connexion:', error);
    return false;
  }
};